import { CardCvcElement, CardExpiryElement, CardNumberElement, useElements, useStripe, Elements } from "@stripe/react-stripe-js";
import { FC, useState, FormEvent } from "react";
import { teamClient } from "lib/clients";
import { Box, Button, Grid, GridItem, Stack, Alert, AlertIcon, AlertDescription, AlertTitle } from "@chakra-ui/react";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import getConfig from "next/config";
import URI from "urijs";
import { CardInput } from "./cardInput";
import { useBilling, useTeam } from "lib/hooks";

const { publicRuntimeConfig: { stripePublic, stripeTeams }} = getConfig();

const stripePromise: Promise<Stripe> = loadStripe(stripePublic);

enum FormFields {
  NUMBER = 'NUMBER',
  CVC = 'CVC',
  EXPIRY = 'EXPIRY',
}

type Props = {
  onSuccess: () => void;
  submitText: string;
}

const StripePaymentForm: FC<Props> = ({ onSuccess, submitText }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { mutate: mutateBilling } = useBilling();
  const [ formHasError, setFormHasError ] = useState<boolean>(false);
  const [ isProcessing, setIsProcessing ] = useState<boolean>(false);
  const { data: team } = useTeam();

  const handleSubmit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();

    // Stripe isn't ready yet
    if (!stripe || !elements) {
      return;
    }

    try {
      setIsProcessing(true);

      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: elements.getElement(CardNumberElement)
      });

      if (error) {
        setFormHasError(true)
      }

      const uri = new URI(window.location);
      const params = uri.search(true);

      const client = team.has_subscription ? teamClient.updateSubscription : async (sub: any) => { 
        try {
          await teamClient.postSubscription(sub);
        }
        catch(e) {
          console.log(e);

          if (e?.response?.status === 409) {
            await teamClient.updateSubscription(sub);
          }
        }
      }

      await client({
        payment_method: paymentMethod.id,
        plan: stripeTeams,
        code: params?.code,
      });

      await mutateBilling();
      onSuccess();
    } catch (err) {
      setFormHasError(true)
    } finally {
      setIsProcessing(false);
    }
  };

  const clearError = () => setFormHasError(false);

  return (
    <Box width="100%" px={10} alignSelf={{ base: 'center', lg: 'flex-start' }} padding={0} mb={[10, 0]}>
      {formHasError && (
        <Alert status='error' fontSize={"sm"} borderRadius={"md"} mb={3}>
          <AlertIcon />
          <AlertTitle>There&apos;s been an error.</AlertTitle>
          <AlertDescription>Please try again.</AlertDescription>
        </Alert>
      )}
      <form onSubmit={handleSubmit}>
        <Stack spacing={4}>
          <Box>
            <CardInput
              label={"Card Number"}
              key={FormFields.NUMBER}
              onChange={clearError}
              as={CardNumberElement}
            />
          </Box>
          <Grid templateColumns={"1fr 1fr"} gap={6}>
            <GridItem>
              <CardInput
                label={"Expiry Date"}
                key={FormFields.EXPIRY}
                onChange={clearError}
                as={CardExpiryElement}
              />
            </GridItem>
            <GridItem>
              <CardInput
                label={"CVC"}
                key={FormFields.CVC}
                onChange={clearError}
                as={CardCvcElement}
              />
            </GridItem>
          </Grid>
          <Box pt={1} width={"100%"}>
            <Button
              variant={"primary"}
              type="submit"
              isDisabled={!stripe || isProcessing}
              isLoading={isProcessing}
              loadingText={"Submitting"}
              size={"xl"}
              width={"100%"}
            >
              { submitText }
            </Button>
          </Box>
        </Stack>
      </form>
    </Box>
  )
}

export const StripeElements: FC<Props> = ({ onSuccess, submitText }) => (
  <Elements stripe={stripePromise}>
    <StripePaymentForm onSuccess={onSuccess} submitText={submitText} />
  </Elements>
);
